import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Trash2, Save, Download, Pencil, ArrowLeftRight, Copy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AlienLogo } from "@/components/AlienLogo";
import { ImageUpload } from "@/components/ImageUpload";
import { SortableList, SortableItem } from "@/components/Sortable";
import { toast } from "sonner";

const tabs = [
  { k: "lives", label: "Live" },
  { k: "merch", label: "Merch" },
  { k: "members", label: "Band" },
  { k: "gallery", label: "Gallery" },
  { k: "subscribers", label: "Newsletter" },
  { k: "config", label: "Config" },
];

export default function Admin() {
  const { user, checked, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("lives");

  useEffect(() => {
    if (checked && !user) nav("/admin/login");
  }, [checked, user, nav]);

  if (!checked || !user) {
    return <div className="min-h-screen flex items-center justify-center text-white/50">Caricamento…</div>;
  }

  return (
    <div className="grain min-h-screen">
      <header className="border-b border-white/10 bg-black/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 md:px-8 py-4">
          <div className="flex items-center gap-3">
            <AlienLogo size={32} />
            <div>
              <p className="text-[color:var(--alien)] uppercase text-[10px] tracking-widest">Admin Panel</p>
              <h1 className="font-display text-xl tracking-widest">TOO MUCH NOISE</h1>
            </div>
          </div>
          <button
            onClick={async () => { await logout(); nav("/admin/login"); }}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-[color:var(--alien)] hover:text-[color:var(--alien)]"
          >
            <LogOut size={14} /> Esci
          </button>
        </div>
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex gap-1 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`admin-tab-${t.k}`}
              className={`px-5 py-3 uppercase tracking-widest text-xs border-b-2 -mb-px whitespace-nowrap ${tab === t.k ? "text-[color:var(--alien)] border-[color:var(--alien)]" : "text-white/50 border-transparent hover:text-white"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        {tab === "lives" && <CrudLives />}
        {tab === "merch" && <CrudMerch />}
        {tab === "members" && <CrudMembers />}
        {tab === "gallery" && <CrudGallery />}
        {tab === "subscribers" && <SubscribersView />}
        {tab === "config" && <ConfigView />}
      </main>
    </div>
  );
}

// ---------- Reusable ----------
function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-white/60 uppercase text-[10px] tracking-widest">{label}</span>
      <input {...props} className="w-full mt-1 bg-transparent border border-white/20 px-3 py-2 text-sm text-white focus:border-[color:var(--alien)] outline-none" />
    </label>
  );
}
function Textarea({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-white/60 uppercase text-[10px] tracking-widest">{label}</span>
      <textarea {...props} className="w-full mt-1 bg-transparent border border-white/20 px-3 py-2 text-sm text-white focus:border-[color:var(--alien)] outline-none min-h-[80px]" />
    </label>
  );
}

// ---------- LIVES ----------
function CrudLives() {
  const emptyForm = { venue: "", address: "", city: "", date: "", map_url: "", notes: "", ticket_url: "" };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/lives").then(({ data }) => setItems(data.sort((a,b) => a.date.localeCompare(b.date))));
  useEffect(() => { load(); }, []);

  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const startEdit = (l) => {
    setEditingId(l.id);
    setForm({
      venue: l.venue || "",
      address: l.address || "",
      city: l.city || "",
      date: toLocalInput(l.date),
      map_url: l.map_url || "",
      notes: l.notes || "",
      ticket_url: l.ticket_url || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const duplicate = (l) => {
    setEditingId(null); // creating new entry on submit
    setForm({
      venue: l.venue || "",
      address: l.address || "",
      city: l.city || "",
      date: "", // must be set fresh
      map_url: l.map_url || "",
      notes: l.notes || "",
      ticket_url: l.ticket_url || "",
    });
    toast("Compila la nuova data e ora, il resto è già copiato.", { icon: "📋" });
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => document.querySelector('[data-testid="live-form-date"]')?.focus(), 400);
  };

  const cancelEdit = () => { setEditingId(null); setForm(emptyForm); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, date: new Date(form.date).toISOString() };
      if (editingId) {
        await api.put(`/lives/${editingId}`, payload);
        toast.success("Data live aggiornata.");
      } else {
        await api.post("/lives", payload);
        toast.success("Data live aggiunta.");
      }
      cancelEdit();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questa data?")) return;
    try { await api.delete(`/lives/${id}`); toast.success("Eliminato."); if (editingId === id) cancelEdit(); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="grid md:grid-cols-[380px_1fr] gap-8">
      <form onSubmit={save} className={`border p-5 space-y-3 ${editingId ? "border-[color:var(--alien)]" : "border-white/10"}`} data-testid="live-form">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-2xl">{editingId ? "Modifica data" : "Nuova data"}</h2>
          {editingId && (
            <button type="button" onClick={cancelEdit} data-testid="live-form-cancel" className="text-white/50 hover:text-white text-xs uppercase tracking-widest border border-white/20 px-2 py-1">
              Annulla
            </button>
          )}
        </div>
        <Field label="Locale" data-testid="live-form-venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} required />
        <Field label="Indirizzo" data-testid="live-form-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        <Field label="Città" data-testid="live-form-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        <Field label="Data/Ora" type="datetime-local" data-testid="live-form-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
        <Field label="Link Mappa" data-testid="live-form-map" value={form.map_url} onChange={(e) => setForm({ ...form, map_url: e.target.value })} />
        <Field label="Link Ticket" data-testid="live-form-ticket" value={form.ticket_url} onChange={(e) => setForm({ ...form, ticket_url: e.target.value })} />
        <Textarea label="Note" data-testid="live-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <button type="submit" data-testid="live-form-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--alien)] text-black font-bold py-2 uppercase tracking-widest text-sm">
          {editingId ? <><Save size={16} /> Salva modifiche</> : <><Plus size={16} /> Aggiungi</>}
        </button>
      </form>
      <div className="space-y-2" data-testid="lives-list">
        {items.map((l, i) => (
          <div key={l.id} className={`border p-4 flex items-center justify-between gap-4 ${editingId === l.id ? "border-[color:var(--alien)]" : "border-white/10"}`}>
            <div className="min-w-0">
              <p className="font-display text-xl truncate">{l.venue} — <span className="text-[color:var(--alien)]">{new Date(l.date).toLocaleDateString("it-IT")}</span></p>
              <p className="text-white/50 text-sm truncate">{l.address}{l.address && l.city ? ", " : ""}{l.city}</p>
              {l.notes && <p className="text-white/40 text-xs truncate">{l.notes}</p>}
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => startEdit(l)} data-testid={`live-edit-${i}`} className="text-[color:var(--alien)] border border-[color:var(--alien)]/50 hover:bg-[color:var(--alien)]/10 px-3 py-1.5 text-xs" aria-label="Modifica" title="Modifica">
                <Pencil size={14} />
              </button>
              <button onClick={() => duplicate(l)} data-testid={`live-duplicate-${i}`} className="text-white/70 border border-white/30 hover:bg-white/10 px-3 py-1.5 text-xs" aria-label="Duplica" title="Duplica (cambia solo data/ora)">
                <Copy size={14} />
              </button>
              <button onClick={() => del(l.id)} data-testid={`live-delete-${i}`} className="text-red-400 hover:text-red-300 border border-red-400/40 px-3 py-1.5 text-xs uppercase tracking-widest" aria-label="Elimina" title="Elimina">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- MERCH ----------
function CrudMerch() {
  const emptyForm = { name: "", description: "", image_url: "", image_url_2: "", main_is_back: false, sizes: "", price_hint: "", available: true, is_new: false };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/merch").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const startEdit = (m) => {
    setEditingId(m.id);
    setForm({
      name: m.name || "",
      description: m.description || "",
      image_url: m.image_url || "",
      image_url_2: m.image_url_2 || "",
      main_is_back: !!m.main_is_back,
      sizes: m.sizes || "",
      price_hint: m.price_hint || "",
      available: m.available !== false,
      is_new: !!m.is_new,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => { setEditingId(null); setForm(emptyForm); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/merch/${editingId}`, form);
        toast.success("Merch aggiornato.");
      } else {
        await api.post("/merch", form);
        toast.success("Merch aggiunto.");
      }
      cancelEdit();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    try { await api.delete(`/merch/${id}`); toast.success("Eliminato."); if (editingId === id) cancelEdit(); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="grid md:grid-cols-[380px_1fr] gap-8">
      <form onSubmit={save} className={`border p-5 space-y-3 ${editingId ? "border-[color:var(--alien)]" : "border-white/10"}`} data-testid="merch-form">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-2xl">{editingId ? "Modifica item" : "Nuovo item"}</h2>
          {editingId && (
            <button type="button" onClick={cancelEdit} data-testid="merch-form-cancel" className="text-white/50 hover:text-white text-xs uppercase tracking-widest border border-white/20 px-2 py-1">
              Annulla
            </button>
          )}
        </div>
        <Field label="Nome" data-testid="merch-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <ImageUpload label={form.main_is_back ? "Foto principale (back)" : "Foto principale (front)"} testId="merch-form-image" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} />
        <ImageUpload label={form.main_is_back ? "Seconda foto (front, opzionale)" : "Seconda foto (back, opzionale)"} testId="merch-form-image-2" value={form.image_url_2} onChange={(v) => setForm({ ...form, image_url_2: v })} />
        {form.image_url && form.image_url_2 && (
          <button
            type="button"
            onClick={() => setForm({ ...form, image_url: form.image_url_2, image_url_2: form.image_url, main_is_back: !form.main_is_back })}
            data-testid="merch-form-swap"
            className="w-full inline-flex items-center justify-center gap-2 border border-[color:var(--alien)] text-[color:var(--alien)] py-2 uppercase tracking-widest text-xs hover:bg-[color:var(--alien)] hover:text-black"
          >
            <ArrowLeftRight size={14} /> Scambia front ↔ back {form.main_is_back ? "(ora vede prima il back)" : "(ora vede prima il front)"}
          </button>
        )}
        <Field label="Taglie / Varianti" data-testid="merch-form-sizes" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} />
        <Field label="Prezzo indicativo" data-testid="merch-form-price" value={form.price_hint} onChange={(e) => setForm({ ...form, price_hint: e.target.value })} />
        <Textarea label="Descrizione" data-testid="merch-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <label className="flex items-center gap-2 text-white/70 text-sm">
          <input type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} data-testid="merch-form-available" /> Disponibile
        </label>
        <label className="flex items-center gap-2 text-[color:var(--alien)] text-sm font-medium">
          <input type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })} data-testid="merch-form-is-new" /> Nuovo arrivo (badge in evidenza)
        </label>
        <button type="submit" data-testid="merch-form-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--alien)] text-black font-bold py-2 uppercase tracking-widest text-sm">
          {editingId ? <><Save size={16} /> Salva modifiche</> : <><Plus size={16} /> Aggiungi</>}
        </button>
      </form>
      <div>
        <p className="text-white/40 text-xs uppercase tracking-widest mb-3">Trascina l'icona per riordinare</p>
        <SortableList
          items={items}
          onReorder={async (reordered) => {
            setItems(reordered);
            try {
              await api.put("/merch/reorder", { ids: reordered.map((m) => m.id) });
              toast.success("Ordine aggiornato.");
            } catch (e) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail));
              load();
            }
          }}
          className="grid sm:grid-cols-2 gap-4"
          data-testid="merch-list"
        >
          {items.map((m, i) => (
            <SortableItem key={m.id} id={m.id} testId={`merch-item-${i}`}>
              <div className={`border pl-12 pr-4 py-4 flex gap-3 bg-[color:var(--bg-panel)] ${editingId === m.id ? "border-[color:var(--alien)]" : "border-white/10"}`}>
                <div className="flex flex-col gap-1">
                  {m.image_url && <img src={m.image_url} alt="" className="w-20 h-20 object-cover" />}
                  {m.image_url_2 && <img src={m.image_url_2} alt="" className="w-20 h-20 object-cover opacity-80" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg truncate">{m.name} {m.price_hint && <span className="text-[color:var(--alien)] text-sm">— {m.price_hint}</span>}</p>
                  {m.is_new && <span className="inline-block bg-[color:var(--alien)] text-black text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 mt-1">Nuovo arrivo</span>}
                  <p className="text-white/50 text-xs mt-1">{m.sizes}</p>
                  <p className="text-white/40 text-xs mt-1 line-clamp-2">{m.description}</p>
                  {m.image_url_2 && <p className="text-[color:var(--alien)] text-[10px] mt-1 uppercase tracking-widest">Front + Back</p>}
                  {!m.available && <p className="text-red-400 text-[10px] mt-1 uppercase tracking-widest">Esaurito</p>}
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => startEdit(m)} data-testid={`merch-edit-${i}`} className="text-[color:var(--alien)] border border-[color:var(--alien)]/50 hover:bg-[color:var(--alien)]/10 px-2 h-8 text-xs" aria-label="Modifica">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => del(m.id)} data-testid={`merch-delete-${i}`} className="text-red-400 border border-red-400/40 px-2 h-8 text-xs" aria-label="Elimina">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </SortableItem>
          ))}
        </SortableList>
      </div>
    </div>
  );
}

// ---------- MEMBERS ----------
function CrudMembers() {
  const emptyForm = { name: "", nickname: "", role: "", bio: "", image_url: "", order: 0 };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/members").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const startEdit = (m) => {
    setEditingId(m.id);
    setForm({
      name: m.name || "",
      nickname: m.nickname || "",
      role: m.role || "",
      bio: m.bio || "",
      image_url: m.image_url || "",
      order: m.order ?? 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditingId(null); setForm(emptyForm); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, order: Number(form.order) };
      if (editingId) {
        await api.put(`/members/${editingId}`, payload);
        toast.success("Membro aggiornato.");
      } else {
        await api.post("/members", payload);
        toast.success("Membro aggiunto.");
      }
      cancelEdit();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    try { await api.delete(`/members/${id}`); toast.success("Eliminato."); if (editingId === id) cancelEdit(); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const onReorder = async (reordered) => {
    setItems(reordered);
    try {
      await api.put("/members/reorder", { ids: reordered.map((m) => m.id) });
      toast.success("Ordine aggiornato.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      load();
    }
  };

  return (
    <div className="grid md:grid-cols-[380px_1fr] gap-8">
      <form onSubmit={save} className={`border p-5 space-y-3 ${editingId ? "border-[color:var(--alien)]" : "border-white/10"}`} data-testid="member-form">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-2xl">{editingId ? "Modifica membro" : "Nuovo membro"}</h2>
          {editingId && (
            <button type="button" onClick={cancelEdit} data-testid="member-form-cancel" className="text-white/50 hover:text-white text-xs uppercase tracking-widest border border-white/20 px-2 py-1">
              Annulla
            </button>
          )}
        </div>
        <Field label="Nome" data-testid="member-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <Field label="Nickname" data-testid="member-form-nick" value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} />
        <Field label="Ruolo" data-testid="member-form-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} required />
        <ImageUpload label="Foto membro" testId="member-form-image" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} />
        <Field label="Ordine" type="number" data-testid="member-form-order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
        <Textarea label="Bio" data-testid="member-form-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
        <button type="submit" data-testid="member-form-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--alien)] text-black font-bold py-2 uppercase tracking-widest text-sm">
          {editingId ? <><Save size={16} /> Salva modifiche</> : <><Plus size={16} /> Aggiungi</>}
        </button>
      </form>
      <div>
        <p className="text-white/40 text-xs uppercase tracking-widest mb-3">Trascina l'icona per riordinare</p>
        <SortableList items={items} onReorder={onReorder} className="space-y-2" data-testid="members-list">
          {items.map((m, i) => (
            <SortableItem key={m.id} id={m.id} testId={`member-item-${i}`}>
              <div className={`border pl-12 pr-4 py-3 flex items-center gap-4 bg-[color:var(--bg-panel)] ${editingId === m.id ? "border-[color:var(--alien)]" : "border-white/10"}`}>
                {m.image_url && <img src={m.image_url} alt="" className="w-14 h-14 object-cover" />}
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg truncate">{m.name} <span className="text-[color:var(--alien)] text-sm">— {m.role}</span></p>
                  {m.nickname && <p className="text-white/50 text-xs truncate">"{m.nickname}"</p>}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button onClick={() => startEdit(m)} data-testid={`member-edit-${i}`} className="text-[color:var(--alien)] border border-[color:var(--alien)]/50 hover:bg-[color:var(--alien)]/10 px-3 py-1.5 text-xs" aria-label="Modifica">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => del(m.id)} data-testid={`member-delete-${i}`} className="text-red-400 border border-red-400/40 px-3 py-1.5 text-xs" aria-label="Elimina">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </SortableItem>
          ))}
        </SortableList>
      </div>
    </div>
  );
}

// ---------- SUBSCRIBERS ----------
function SubscribersView() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/subscribers").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/subscribers/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "subscribers.csv"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare iscritto?")) return;
    try { await api.delete(`/subscribers/${id}`); toast.success("Eliminato."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-white/60 text-sm"><span className="text-[color:var(--alien)] font-bold">{items.length}</span> iscritti</p>
        <button onClick={exportCsv} data-testid="subscribers-export-btn" className="inline-flex items-center gap-2 border border-[color:var(--alien)] text-[color:var(--alien)] px-4 py-2 text-xs uppercase tracking-widest hover:bg-[color:var(--alien)] hover:text-black">
          <Download size={14} /> Export CSV
        </button>
      </div>
      <div className="border border-white/10" data-testid="subscribers-list">
        {items.length === 0 && <p className="text-white/40 p-8 text-center text-sm">Nessun iscritto ancora.</p>}
        {items.map((s, i) => (
          <div key={s.id} className="flex items-center justify-between px-4 py-3 border-b border-white/5 last:border-b-0">
            <div>
              <p className="text-white text-sm">{s.email}</p>
              <p className="text-white/40 text-xs">{new Date(s.created_at).toLocaleString("it-IT")}</p>
            </div>
            <button onClick={() => del(s.id)} data-testid={`subscriber-delete-${i}`} className="text-red-400 border border-red-400/40 px-3 py-1.5 text-xs">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- GALLERY ----------
function CrudGallery() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ image_url: "", caption: "", event: "", order: 0 });
  const load = () => api.get("/gallery").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.image_url) { toast.error("Carica un'immagine prima."); return; }
    try { await api.post("/gallery", { ...form, order: Number(form.order) }); toast.success("Foto aggiunta."); setForm({ image_url: "", caption: "", event: "", order: 0 }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare foto?")) return;
    try { await api.delete(`/gallery/${id}`); toast.success("Eliminata."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const onReorder = async (reordered) => {
    setItems(reordered);
    try {
      await api.put("/gallery/reorder", { ids: reordered.map((p) => p.id) });
      toast.success("Ordine aggiornato.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      load();
    }
  };

  return (
    <div className="grid md:grid-cols-[380px_1fr] gap-8">
      <form onSubmit={save} className="border border-white/10 p-5 space-y-3" data-testid="gallery-form">
        <h2 className="font-display text-2xl mb-2">Nuova foto</h2>
        <ImageUpload label="Foto" testId="gallery-form-image" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} />
        <Field label="Evento (es. Live @ Colorificio 2024)" data-testid="gallery-form-event" value={form.event} onChange={(e) => setForm({ ...form, event: e.target.value })} />
        <Field label="Didascalia" data-testid="gallery-form-caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} />
        <Field label="Ordine" type="number" data-testid="gallery-form-order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
        <button type="submit" data-testid="gallery-form-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--alien)] text-black font-bold py-2 uppercase tracking-widest text-sm">
          <Plus size={16} /> Aggiungi
        </button>
      </form>
      <div>
        <p className="text-white/40 text-xs uppercase tracking-widest mb-3">Trascina l'icona per riordinare</p>
        {items.length === 0 ? (
          <p className="text-white/40 col-span-full text-center py-10 border border-dashed border-white/15" data-testid="gallery-list">Nessuna foto in gallery.</p>
        ) : (
          <SortableList items={items} onReorder={onReorder} strategy="grid" className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="gallery-list">
            {items.map((p, i) => (
              <SortableItem key={p.id} id={p.id} testId={`gallery-item-${i}`}>
                <div className="relative group border border-white/10">
                  <img src={p.image_url} alt="" className="w-full aspect-square object-cover" />
                  <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black to-transparent">
                    {p.event && <p className="text-[color:var(--alien)] text-[10px] uppercase tracking-widest">{p.event}</p>}
                    {p.caption && <p className="text-white text-xs line-clamp-1">{p.caption}</p>}
                  </div>
                  <button onClick={() => del(p.id)} data-testid={`gallery-delete-${i}`} className="absolute top-2 right-2 bg-red-500/80 text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 size={14} />
                  </button>
                </div>
              </SortableItem>
            ))}
          </SortableList>
        )}
      </div>
    </div>
  );
}

// ---------- CONFIG ----------
function ConfigView() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/site-config").then(({ data }) => setCfg(data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/site-config", cfg); toast.success("Config salvata."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!cfg) return null;
  const upd = (k) => (e) => setCfg({ ...cfg, [k]: e.target.value });

  return (
    <form onSubmit={save} className="max-w-2xl space-y-5" data-testid="config-form">
      <h2 className="font-display text-3xl">Configurazione sito</h2>
      <ImageUpload label="Immagine Hero Home" testId="config-hero-upload" value={cfg.hero_image_url} onChange={(v) => setCfg({ ...cfg, hero_image_url: v })} />
      <ImageUpload label="Logo Band (accanto a 'La Squadra dei Too Much')" testId="config-band-logo-upload" value={cfg.band_logo_url} onChange={(v) => setCfg({ ...cfg, band_logo_url: v })} />
      <ImageUpload label="Logo Footer (accanto a 'Sorry for the Noise')" testId="config-footer-logo-upload" value={cfg.footer_logo_url} onChange={(v) => setCfg({ ...cfg, footer_logo_url: v })} />

      <div className="border-t border-white/10 pt-5 space-y-4">
        <h3 className="font-display text-xl text-[color:var(--alien)] uppercase tracking-widest">Testi Hero</h3>
        <Field label="Tagline (sopra il titolo)" data-testid="config-hero-tagline" value={cfg.hero_tagline || ""} onChange={upd("hero_tagline")} />
        <Textarea label="Testo introduttivo (sotto il titolo)" data-testid="config-hero-intro" value={cfg.hero_intro || ""} onChange={upd("hero_intro")} />
      </div>

      <div className="border-t border-white/10 pt-5 space-y-4">
        <h3 className="font-display text-xl text-[color:var(--alien)] uppercase tracking-widest">Sezione Band</h3>
        <Textarea label="Titolo (usa Invio per andare a capo)" data-testid="config-band-title" value={cfg.band_title || ""} onChange={upd("band_title")} />
        <Textarea label="Testo introduttivo" data-testid="config-band-intro" value={cfg.band_intro || ""} onChange={upd("band_intro")} />
      </div>

      <div className="border-t border-white/10 pt-5 space-y-4">
        <h3 className="font-display text-xl text-[color:var(--alien)] uppercase tracking-widest">Titoli Altre Sezioni</h3>
        <Textarea label="Titolo Live (usa Invio per andare a capo)" data-testid="config-live-title" value={cfg.live_title || ""} onChange={upd("live_title")} />
        <Textarea label="Titolo Merch (usa Invio per andare a capo)" data-testid="config-merch-title" value={cfg.merch_title || ""} onChange={upd("merch_title")} />
        <Textarea label="Titolo Gallery Foto Live (usa Invio per andare a capo)" data-testid="config-gallery-title" value={cfg.gallery_title || ""} onChange={upd("gallery_title")} />
        <Field label="Sottotitolo Gallery" data-testid="config-gallery-intro" value={cfg.gallery_intro || ""} onChange={upd("gallery_intro")} />
      </div>

      <div className="border-t border-white/10 pt-5 space-y-4">
        <h3 className="font-display text-xl text-[color:var(--alien)] uppercase tracking-widest">Footer</h3>
        <Textarea label="Scritta grande footer (ultima riga in arancione, usa Invio per andare a capo)" data-testid="config-footer-tagline" value={cfg.footer_tagline || ""} onChange={upd("footer_tagline")} />
      </div>

      <div className="border-t border-white/10 pt-5 space-y-4">
        <h3 className="font-display text-xl text-[color:var(--alien)] uppercase tracking-widest">Sezione "I Nostri Pezzi"</h3>
        <Field label="Titolo singolo" data-testid="config-single-title" value={cfg.single_title} onChange={upd("single_title")} />
      <Textarea label="Descrizione singolo" data-testid="config-single-desc" value={cfg.single_description} onChange={upd("single_description")} />
      <Field label="URL Cover singolo" data-testid="config-single-cover" value={cfg.single_cover_url} onChange={upd("single_cover_url")} />
      <ImageUpload label="Oppure carica cover" testId="config-single-cover-upload" value={cfg.single_cover_url} onChange={(v) => setCfg({ ...cfg, single_cover_url: v })} />
      <Field label="Link Spotify singolo" data-testid="config-single-spotify" value={cfg.single_spotify_url} onChange={upd("single_spotify_url")} />
      </div>

      <div className="border-t border-white/10 pt-5 space-y-4">
        <h3 className="font-display text-xl text-[color:var(--alien)] uppercase tracking-widest">Social & Contatti</h3>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Instagram" data-testid="config-instagram" value={cfg.instagram_url} onChange={upd("instagram_url")} />
        <Field label="Spotify" data-testid="config-spotify" value={cfg.spotify_url} onChange={upd("spotify_url")} />
        <Field label="YouTube" data-testid="config-youtube" value={cfg.youtube_url} onChange={upd("youtube_url")} />
        <Field label="Facebook" data-testid="config-facebook" value={cfg.facebook_url} onChange={upd("facebook_url")} />
      </div>
      <Field label="Email contatto" data-testid="config-email" value={cfg.contact_email} onChange={upd("contact_email")} />
      </div>
      <button type="submit" data-testid="config-submit" className="inline-flex items-center gap-2 bg-[color:var(--alien)] text-black font-bold px-6 py-3 uppercase tracking-widest text-sm">
        <Save size={16} /> Salva
      </button>
    </form>
  );
}
